import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useData } from "../../context/DataContext";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { api } from "../../utils/api";
import { toSydneyDate } from "../../utils/helpers";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Checkbox } from "../../components/ui/checkbox";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Calendar as CalendarPrimitive } from "../../components/ui/calendar";
import { MultiSelect } from "../../components/ui/multi-select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../components/ui/popover";
import {
  ChevronLeft,
  CalendarDays,
  User,
  Briefcase,
  Building2,
  Mail,
  CheckCircle2,
  Loader2,
  Save,
  Monitor,
  MessageSquare,
  FolderOpen,
  Wrench,
  Package,
  X,
  Plus,
  Smartphone,
  Shield,
} from "lucide-react";
import { RequirementCategory, INITIAL_CATEGORIES, mapRequirementCategoryFromApi } from "../../types/onboarding-requirements";

interface Candidate {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string | null;
  currentPosition: string | null;
  currentCompany: string | null;
  status: string;
  requisition: {
    id: number;
    jobRole: { id: number; roleName: string } | null;
    department: { id: number; departmentName: string } | null;
    reportsTo: { id: number; name: string } | null;
    jobPositions: Array<{ id: number; positionCode: string }> | null;
  } | null;
  jobPosition: { id: number; positionCode: string } | null;
}

interface OnboardingTemplate {
  id: number;
  name: string;
  description: string;
  // Requirement item ids keyed by category id; null = template follows the
  // live Onboarding Settings > Requirements list
  requirements?: Record<string, string[]> | null;
}

interface TrainingPlan {
  id: number;
  name: string;
}

interface OnboardingDraft {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  startDate: string | null;
  onboardingTemplate: string | null;
  trainingPlanId: number | null;
  trainingPlanIds: number[] | null;
  notesForItHrFinance: string | null;
  draftData: {
    startDate?: string;
    onboardingTemplate?: string;
    trainingPlanId?: string;
    trainingPlanIds?: number[];
    notesForItHrFinance?: string;
    userRole?: string;
    employmentStatus?: string;
    managerId?: number | null;
    showInAttendance?: boolean;
    accrualStartDate?: string | null;
    sharePointGroups?: string[];
    msTeamsGroups?: string[];
    // Selected requirement item ids keyed by category id
    requirements?: Record<string, string[]>;
    itEquipment?: string[];
    softwareAccess?: string[];
    tools?: string[];
    securityAccess?: string[];
  } | null;
  userRole: string | null;
  employmentStatus: string | null;
  isDraft: boolean;
}

const STEPS = [
  { number: 1, label: "Confirm Details" },
  { number: 2, label: "Setup & Access" },
  { number: 3, label: "Review & Confirm" },
];

const CategoryIcon: React.FC<{ icon: RequirementCategory["icon"]; className?: string }> = ({ icon, className }) => {
  switch (icon) {
    case "monitor": return <Monitor className={className} />;
    case "smartphone": return <Smartphone className={className} />;
    case "shield": return <Shield className={className} />;
    case "wrench": return <Wrench className={className} />;
    default: return <Package className={className} />;
  }
};

const CommenceHiring: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { candidateId } = useParams<{ candidateId: string }>();
  const { currentUser } = useAuth();
  const { userRoles, managerUsers, fetchManagerUsers } = useData();

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [draftId, setDraftId] = useState<number | null>(null);

  // Step 1 fields
  const [startDate, setStartDate] = useState("");
  const [username, setUsername] = useState("");
  const [onboardingTemplate, setOnboardingTemplate] = useState("");
  const [trainingPlanIds, setTrainingPlanIds] = useState<string[]>([]);
  const [notes, setNotes] = useState("");
  const [userRole, setUserRole] = useState("Worker");
  const [managerId, setManagerId] = useState("");
  const [employmentStatus, setEmploymentStatus] = useState("Permanent FT");
  const [showInAttendance, setShowInAttendance] = useState(false);
  const [accrualStartDate, setAccrualStartDate] = useState("");

  // Step 3 fields
  const [confirmReview, setConfirmReview] = useState(false);

  // Step 2 - Setup & Access
  const [sharePointGroups, setSharePointGroups] = useState<string[]>([]);
  const [msTeamsGroups, setMsTeamsGroups] = useState<string[]>([]);
  // selectedItems: map of categoryId -> Set of itemIds
  const [selectedItems, setSelectedItems] = useState<Record<string, Set<string>>>({});
  const [newSharePointGroup, setNewSharePointGroup] = useState("");
  const [newMsTeamsGroup, setNewMsTeamsGroup] = useState("");

  // Requirement categories (fetched from settings or use defaults)
  const [requirementCategories, setRequirementCategories] = useState<RequirementCategory[]>(INITIAL_CATEGORIES);

  // Data
  const [templates, setTemplates] = useState<OnboardingTemplate[]>([]);
  const [trainingPlans, setTrainingPlans] = useState<TrainingPlan[]>([]);

  const canCreateOnboarding = ability.can(ACTIONS.CREATE, RESOURCES.ONBOARDING_REQUEST);

  // Manager dropdown options — ensure the requisition's reports-to user is always listed
  const managerOptions = useMemo(() => {
    const options: Array<{ id: string; name: string; jobRole: string }> = managerUsers.map((u) => ({
      id: String(u.id),
      name: u.name,
      jobRole: u.jobRole || "",
    }));
    const reportsTo = candidate?.requisition?.reportsTo;
    if (reportsTo && !options.some((u) => u.id === String(reportsTo.id))) {
      options.unshift({ id: String(reportsTo.id), name: reportsTo.name, jobRole: "" });
    }
    return options;
  }, [managerUsers, candidate]);

  useEffect(() => {
    if (managerUsers.length === 0) fetchManagerUsers();
  }, []);

  // Load the requirement categories configured in Onboarding Settings.
  // Falls back to the bundled defaults if the fetch fails.
  useEffect(() => {
    const fetchRequirementCategories = async () => {
      try {
        const res = await api.get<{ success: boolean; data: any[] }>(
          "/api/onboarding/requirements/categories",
          { skipErrorToast: true }
        );
        if (res.success && res.data && res.data.length > 0) {
          setRequirementCategories(res.data.map(mapRequirementCategoryFromApi));
        }
      } catch (error) {
        console.error("Error fetching requirement categories:", error);
        // Keep default categories
      }
    };
    fetchRequirementCategories();
  }, []);

  // Load draft from database
  useEffect(() => {
    if (!candidateId) return;
    loadDraftFromDB();
  }, [candidateId]);

  const loadDraftFromDB = async () => {
    try {
      const res = await api.get<{ success: boolean; data: OnboardingDraft | null }>(
        `/api/onboarding/draft/${candidateId}`,
        { skipErrorToast: true }
      );
      if (res.success && res.data) {
        const draft = res.data;
        setDraftId(draft.id);
        setStartDate(draft.startDate || "");
        // Only restore the draft's username when present — fetchData fills in
        // the candidate's email otherwise, and this keeps the draft's value
        // regardless of which request resolves last.
        if (draft.email) setUsername(draft.email);
        setOnboardingTemplate(draft.onboardingTemplate || "");
        // Multi-plan drafts store an array; legacy drafts only have the
        // single trainingPlanId.
        setTrainingPlanIds(
          Array.isArray(draft.trainingPlanIds) && draft.trainingPlanIds.length > 0
            ? draft.trainingPlanIds.map(String)
            : draft.trainingPlanId
            ? [String(draft.trainingPlanId)]
            : []
        );
        setNotes(draft.notesForItHrFinance || "");
        setUserRole(draft.userRole || "Worker");
        setEmploymentStatus(draft.employmentStatus || "Permanent FT");

        if (draft.draftData) {
          setManagerId(draft.draftData.managerId ? String(draft.draftData.managerId) : "");
          setShowInAttendance(draft.draftData.showInAttendance === true);
          setAccrualStartDate(draft.draftData.accrualStartDate || "");
          setSharePointGroups(draft.draftData.sharePointGroups || []);
          setMsTeamsGroups(draft.draftData.msTeamsGroups || []);
          const restored: Record<string, Set<string>> = {};
          if (draft.draftData.requirements) {
            // New shape: item ids keyed by requirement category id
            Object.entries(draft.draftData.requirements).forEach(([categoryId, itemIds]) => {
              restored[categoryId] = new Set(itemIds);
            });
          } else {
            // Legacy drafts stored four fixed buckets with hardcoded category ids
            (draft.draftData.itEquipment || []).forEach((id: string) => {
              restored["it-equipment"] = restored["it-equipment"] || new Set();
              restored["it-equipment"].add(id);
            });
            (draft.draftData.softwareAccess || []).forEach((id: string) => {
              restored["software"] = restored["software"] || new Set();
              restored["software"].add(id);
            });
            (draft.draftData.tools || []).forEach((id: string) => {
              restored["tools"] = restored["tools"] || new Set();
              restored["tools"].add(id);
            });
            (draft.draftData.securityAccess || []).forEach((id: string) => {
              restored["security"] = restored["security"] || new Set();
              restored["security"].add(id);
            });
          }
          setSelectedItems(restored);
        }
        setShowDraftBanner(true);
      }
    } catch (error) {
      console.error("Error loading draft:", error);
    }
  };

  useEffect(() => {
    if (!candidateId) return;

    const fetchData = async () => {
      try {
        const [candidateRes, templatesRes] = await Promise.all([
          api.get<{ success: boolean; candidate: Candidate }>(`/api/candidates/${candidateId}`),
          api.get<{ success: boolean; data: OnboardingTemplate[] }>("/api/onboarding/templates", { skipErrorToast: true }),
        ]);

        if (candidateRes.success) {
          // Guard: hiring can only commence from OFFER_ACCEPTED (the
          // /api/onboarding/commence endpoint enforces the same). Block
          // direct URL access for candidates already hired or not ready
          // and send the user back to the candidate page.
          const candidateStatus = candidateRes.candidate.status;
          if (candidateStatus !== "OFFER_ACCEPTED") {
            toast.error(
              candidateStatus === "HIRING_COMMENCED" || candidateStatus === "HIRED"
                ? "Hiring has already commenced for this candidate"
                : "This candidate is not ready to commence hiring (offer must be accepted first)"
            );
            navigate(`/recruitment/candidates/${candidateId}`, { replace: true });
            return;
          }
          setCandidate(candidateRes.candidate);
          // Functional update so a username restored from a draft (loaded
          // concurrently by loadDraftFromDB) is not overwritten.
          setUsername((prev) => prev || candidateRes.candidate.email);
          const reportsToId = candidateRes.candidate.requisition?.reportsTo?.id;
          if (reportsToId) {
            // Only prefill if not already set (e.g. restored from a draft)
            setManagerId((prev) => prev || String(reportsToId));
          }
        }

        if (templatesRes.success && templatesRes.data) {
          setTemplates(templatesRes.data);
        } else {
          toast.error("Failed to load onboarding templates");
        }

        // Training plans are allocated from Training Plan Templates, not
        // live plans — the candidate has no user account yet, so no live
        // plan could be assigned to them. Templates assigned to the
        // candidate's job role are preferred; the API falls back to all
        // active templates. On commence, each selected template becomes a
        // live plan for the new hire titled "[Template Name] - [Candidate]".
        const jobRoleId = candidateRes.success
          ? candidateRes.candidate.requisition?.jobRole?.id
          : undefined;
        const plansRes = await api.get<{ success: boolean; templates: Array<{ id: number; templateName: string }> }>(
          `/api/trainingplan-templates/selection${jobRoleId ? `?jobRoleId=${jobRoleId}` : ""}`,
          { skipErrorToast: true }
        );
        if (plansRes.success && plansRes.templates) {
          setTrainingPlans(
            plansRes.templates.map((t) => ({ id: t.id, name: t.templateName }))
          );
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load candidate data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [candidateId]);

  // Serialize selectedItems (categoryId -> Set<itemId>) into the plain
  // object the API stores on the onboarding request.
  const buildRequirementsPayload = (): Record<string, string[]> =>
    Object.fromEntries(
      Object.entries(selectedItems).map(([categoryId, itemIds]) => [categoryId, Array.from(itemIds)])
    );

  // Save draft to database
  const saveDraft = async () => {
    if (!candidateId || !candidate) return;
    setSaving(true);
    try {
      const draftPayload = {
        candidateId: parseInt(candidateId),
        firstName: candidate.firstName,
        lastName: candidate.lastName,
        email: username,
        startDate: startDate || null,
        jobPositionId: candidate.jobPosition?.id || candidate.requisition?.jobPositions?.[0]?.id || null,
        managerId: managerId ? parseInt(managerId) : null,
        offerId: null,
        onboardingTemplate: onboardingTemplate || null,
        trainingPlanIds: trainingPlanIds.map((id) => parseInt(id, 10)),
        notesForItHrFinance: notes || null,
        sharePointGroups,
        msTeamsGroups,
        requirements: buildRequirementsPayload(),
        userRole,
        employmentStatus,
        showInAttendance,
        accrualStartDate: showInAttendance ? accrualStartDate || null : null,
      };

      const res = await api.post<{ success: boolean; data: OnboardingDraft; message: string }>(
        "/api/onboarding/draft",
        draftPayload
      );

      if (res.success) {
        setDraftId(res.data.id);
        toast.success("Draft saved");
        navigate(`/recruitment/candidates/${candidateId}`);
      } else {
        toast.error(res.message || "Failed to save draft");
      }
    } catch (error: any) {
      console.error("Error saving draft:", error);
      toast.error(error.message || "Failed to save draft");
    } finally {
      setSaving(false);
    }
  };

  const clearDraft = async () => {
    if (!draftId) {
      setShowDraftBanner(false);
      return;
    }
    try {
      await api.delete(`/api/onboarding/${draftId}`);
      setShowDraftBanner(false);
      setDraftId(null);
      toast.success("Draft cleared");
    } catch (error) {
      console.error("Error clearing draft:", error);
      // Just hide the banner locally if delete fails
      setShowDraftBanner(false);
    }
  };

  const handleNext = () => {
    if (step === 1) {
      if (!startDate) {
        toast.error("Start date is required");
        return;
      }
      if (!username) {
        toast.error("Username (email) is required");
        return;
      }
      if (!onboardingTemplate) {
        // Without a template the new hire's portal has no forms, policies,
        // licences or support network — there is nothing to onboard with.
        toast.error("Please select an onboarding template");
        return;
      }
    }
    setStep((prev) => Math.min(prev + 1, 3));
  };

  const handleBack = () => {
    setStep((prev) => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!candidate) return;
    if (!confirmReview) {
      toast.error("Please confirm that you have reviewed all details");
      return;
    }

    setSaving(true);
    try {
      const res = await api.post<{ success: boolean; data: any; message: string }>(
        "/api/onboarding/commence",
        {
          firstName: candidate.firstName,
          lastName: candidate.lastName,
          email: username,
          startDate,
          jobPositionId: candidate.jobPosition?.id || candidate.requisition?.jobPositions?.[0]?.id || null,
          managerId: managerId ? parseInt(managerId) : null,
          candidateId: candidate.id,
          onboardingTemplate,
          trainingPlanIds: trainingPlanIds.map((id) => parseInt(id, 10)),
          notesForItHrFinance: notes,
          sharePointGroups,
          msTeamsGroups,
          requirements: buildRequirementsPayload(),
          userRole,
          employmentStatus,
          showInAttendance,
          accrualStartDate: showInAttendance ? accrualStartDate || null : null,
        }
      );

      if (!res.success) {
        toast.error(res.message || "Failed to commence hiring");
        setSaving(false);
        return;
      }

      // No draft cleanup needed here: when a draft exists, the commence
      // endpoint converts that draft row into the live onboarding request
      // (same id), so deleting it would remove the request we just created.

      toast.success("Candidate hired and onboarding commenced successfully!");
      navigate("/onboarding");
    } catch (error: any) {
      console.error("Error in commence hiring:", error);
      toast.error(error.message || "An error occurred");
    } finally {
      setSaving(false);
    }
  };

  const toggleRequirementItem = (categoryId: string, itemId: string) => {
    setSelectedItems((prev) => {
      const next = { ...prev };
      const catSet = new Set(next[categoryId] || []);
      if (catSet.has(itemId)) {
        catSet.delete(itemId);
      } else {
        catSet.add(itemId);
      }
      if (catSet.size === 0) {
        delete next[categoryId];
      } else {
        next[categoryId] = catSet;
      }
      return next;
    });
  };

  // Pre-select Setup & Access items from the chosen onboarding template.
  // A template with NULL requirements follows the live settings list, so all
  // current items are selected; an Admin-curated selection is applied as
  // stored, ignoring items that no longer exist in settings.
  const applyTemplateRequirements = (templateId: string) => {
    const template = templates.find((t) => String(t.id) === templateId);
    if (!template) return;

    const next: Record<string, Set<string>> = {};
    requirementCategories.forEach((category) => {
      const preset = template.requirements?.[category.id];
      const ids = preset
        ? category.items.map((i) => i.id).filter((id) => preset.includes(id))
        : category.items.map((i) => i.id);
      if (ids.length > 0) next[category.id] = new Set(ids);
    });
    setSelectedItems(next);
  };

  const addSharePointGroup = () => {
    if (newSharePointGroup.trim()) {
      setSharePointGroups((prev) => [...prev, newSharePointGroup.trim()]);
      setNewSharePointGroup("");
    }
  };

  const addMsTeamsGroup = () => {
    if (newMsTeamsGroup.trim()) {
      setMsTeamsGroups((prev) => [...prev, newMsTeamsGroup.trim()]);
      setNewMsTeamsGroup("");
    }
  };

  const removeSharePointGroup = (group: string) => {
    setSharePointGroups((prev) => prev.filter((g) => g !== group));
  };

  const removeMsTeamsGroup = (group: string) => {
    setMsTeamsGroups((prev) => prev.filter((g) => g !== group));
  };

  if (!canCreateOnboarding) {
    return <AccessRestricted />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <p>Candidate not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/recruitment")}>
          Back to Recruitment
        </Button>
      </div>
    );
  }

  const candidateName = `${candidate.firstName} ${candidate.lastName}`;
  const role = candidate.requisition?.jobRole?.roleName || candidate.currentPosition || "Role not set";
  // Prefer the candidate's allocated position; fall back to the requisition's position
  const position = candidate.jobPosition?.positionCode || candidate.requisition?.jobPositions?.[0]?.positionCode || "Position not set";
  const department = candidate.requisition?.department?.departmentName || "Department not set";
  const selectedManagerName = managerOptions.find((u) => u.id === managerId)?.name;

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(`/recruitment/candidates/${candidateId}`)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ChevronLeft size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Commence Hiring</h1>
            <p className="text-sm text-gray-500">Set up onboarding for {candidateName}</p>
          </div>
        </div>
      </div>

      {/* Draft Banner */}
      {showDraftBanner && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Save size={18} className="text-amber-600" />
            <p className="text-sm text-amber-800">
              You have a saved draft for this candidate. Your progress has been restored.
            </p>
          </div>
          <button
            onClick={clearDraft}
            className="text-sm text-amber-700 hover:text-amber-900 font-medium"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Step Indicator */}
      <div className="flex items-center gap-2">
        {STEPS.map((s, index) => (
          <React.Fragment key={s.number}>
            <div className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              step === s.number
                ? "bg-slate-900 text-white"
                : step > s.number
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-500"
            }`}>
              {step > s.number ? (
                <CheckCircle2 size={16} />
              ) : (
                <span className="text-sm font-semibold">{s.number}</span>
              )}
              <span className="text-sm font-medium">{s.label}</span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`w-8 h-0.5 ${step > s.number ? "bg-green-500" : "bg-gray-200"}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Step Content */}
      <div className="space-y-6">
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Confirm Details</h2>
              <p className="text-sm text-gray-500 mt-1">Review and confirm the candidate's details before proceeding</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Candidate Info Card */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
                  <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                    <User size={20} className="text-slate-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{candidateName}</h3>
                    <p className="text-sm text-gray-500">{role}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-400 font-bold uppercase tracking-wider">Full Name</label>
                    <p className="text-sm font-medium text-gray-900 mt-1">{candidateName}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-bold uppercase tracking-wider">Role</label>
                    <p className="text-sm font-medium text-gray-900 mt-1">{role}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-bold uppercase tracking-wider">Position</label>
                    <p className="text-sm font-medium text-gray-900 mt-1">{position}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-bold uppercase tracking-wider">Department</label>
                    <p className="text-sm font-medium text-gray-900 mt-1">{department}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-bold uppercase tracking-wider">Email</label>
                    <p className="text-sm font-medium text-gray-900 mt-1">{candidate.email}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-400 font-bold uppercase tracking-wider">Mobile</label>
                    <p className="text-sm font-medium text-gray-900 mt-1">{candidate.phone || "Not provided"}</p>
                  </div>
                </div>
              </div>

              {/* Right: Form Fields */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-5">
                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">
                    Start Date <span className="text-red-500">*</span>
                  </label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full h-11 rounded-xl border-gray-200 bg-white text-sm font-normal justify-start text-left"
                      >
                        <CalendarDays size={16} className="mr-2 text-gray-400" />
                        {toSydneyDate(startDate) || "Select start date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <CalendarPrimitive
                        mode="single"
                        selected={startDate ? new Date(startDate + "T00:00:00") : undefined}
                        onSelect={(date) => {
                          if (date) {
                            const y = date.getFullYear();
                            const m = String(date.getMonth() + 1).padStart(2, "0");
                            const d = String(date.getDate()).padStart(2, "0");
                            setStartDate(`${y}-${m}-${d}`);
                          }
                        }}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">
                    Username (email address) <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="email"
                    placeholder="e.g. admin@example.com"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="h-11 rounded-xl border-gray-200"
                  />
                  <p className="text-xs text-gray-500">This is the email address the candidate will use to sign into Idarah.</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">
                    Onboarding Template <span className="text-red-500">*</span>
                  </label>
                  <Select
                    value={onboardingTemplate}
                    onValueChange={(value) => {
                      setOnboardingTemplate(value);
                      applyTemplateRequirements(value);
                    }}
                  >
                    <SelectTrigger className="h-11 rounded-xl border-gray-200 bg-white">
                      <SelectValue placeholder="Select a template" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={String(t.id)}>
                          <div className="flex flex-col">
                            <span>{t.name}</span>
                            <span className="text-xs text-gray-500">{t.description}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Defines the forms, policies and learning modules the new hire will see, and pre-selects the Setup &amp; Access requirements</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">Training Plans</label>
                  <MultiSelect
                    options={trainingPlans.map((p) => ({ value: String(p.id), label: p.name }))}
                    selected={trainingPlanIds}
                    onChange={setTrainingPlanIds}
                    placeholder="Select training plans"
                    emptyMessage="No training plans found."
                    className="min-h-11 rounded-xl border-gray-200 bg-white"
                  />
                  <p className="text-xs text-gray-500">Select one or more Training Plan Templates. Each becomes a Training Plan for the new hire, titled "[Template Name] - [Candidate Name]".</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">User Role</label>
                  <Select value={userRole} onValueChange={setUserRole}>
                    <SelectTrigger className="h-11 rounded-xl border-gray-200 bg-white">
                      <SelectValue placeholder="Select a user role" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      {userRoles.map((r) => (
                        <SelectItem key={r} value={r}>
                          {r}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Determines what the new hire can access in Idarah.</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">Manager</label>
                  <Select
                    value={managerId || "none"}
                    onValueChange={(value) => setManagerId(value === "none" ? "" : value)}
                  >
                    <SelectTrigger className="h-11 rounded-xl border-gray-200 bg-white">
                      <SelectValue placeholder="Select a manager" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="none">No Manager</SelectItem>
                      {managerOptions.map((u) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name}{u.jobRole ? ` (${u.jobRole})` : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">The person this new hire will report to.</p>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">Employment Status</label>
                  <Select value={employmentStatus} onValueChange={setEmploymentStatus}>
                    <SelectTrigger className="h-11 rounded-xl border-gray-200 bg-white">
                      <SelectValue placeholder="Select employment status" />
                    </SelectTrigger>
                    <SelectContent className="rounded-xl">
                      <SelectItem value="Permanent FT">Permanent FT</SelectItem>
                      <SelectItem value="Permanent PT">Permanent PT</SelectItem>
                      <SelectItem value="Casual">Casual</SelectItem>
                      <SelectItem value="Maximum Term FT">Maximum Term FT</SelectItem>
                      <SelectItem value="Maximum Term PT">Maximum Term PT</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-bold text-gray-900">Notes for IT / HR / Finance</label>
                  <Textarea
                    placeholder="Any special requirements or context for the provisioning teams..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="min-h-[100px] rounded-xl border-gray-200 resize-y"
                  />
                </div>

                <div className="space-y-3 border-t border-gray-100 pt-4">
                  <div className="flex items-center gap-3">
                    <Switch
                      id="showInAttendance"
                      checked={showInAttendance}
                      onCheckedChange={setShowInAttendance}
                    />
                    <label htmlFor="showInAttendance" className="text-sm font-bold text-gray-900 cursor-pointer">
                      Show in Attendance & Leave
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    Enable this if the worker should appear in the Attendance and Leave modules. Recommended for full-time workers. Not recommended for casual workers.
                  </p>
                  {showInAttendance && (
                    <div className="space-y-1.5 pt-1">
                      <label className="text-sm font-bold text-gray-900">Start Accruing From</label>
                      <Input
                        type="date"
                        value={accrualStartDate}
                        onChange={(e) => setAccrualStartDate(e.target.value)}
                        className="h-11 rounded-xl border-gray-200"
                      />
                      <p className="text-xs text-gray-500">
                        Leave will begin accruing from this date. Accruals run daily, based on the person's standard daily hours.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Setup & Access</h2>
              <p className="text-sm text-gray-500 mt-1">Select the equipment, software and access required for this role</p>
            </div>

            <div className="space-y-6">
              {/* SharePoint Groups & MS Teams - Tag Style */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* SharePoint Groups */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <FolderOpen size={18} className="text-blue-500" />
                    <h3 className="font-semibold text-gray-900">SharePoint Groups</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 min-h-[36px]">
                    {sharePointGroups.map((group) => (
                      <span
                        key={group}
                        className="inline-flex items-center gap-1.5 bg-blue-50 text-blue-700 text-sm px-3 py-1.5 rounded-full border border-blue-100"
                      >
                        {group}
                        <button
                          onClick={() => removeSharePointGroup(group)}
                          className="text-blue-400 hover:text-blue-600 transition-colors"
                        >
                          <X size={13} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type a group name and press Enter..."
                      value={newSharePointGroup}
                      onChange={(e) => setNewSharePointGroup(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addSharePointGroup())}
                      className="rounded-xl border-gray-200"
                    />
                    <Button variant="outline" size="sm" onClick={addSharePointGroup} className="rounded-xl shrink-0">
                      <Plus size={16} />
                    </Button>
                  </div>
                </div>

                {/* MS Teams Groups */}
                <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                  <div className="flex items-center gap-2">
                    <MessageSquare size={18} className="text-purple-500" />
                    <h3 className="font-semibold text-gray-900">MS Teams / Group Chats</h3>
                  </div>
                  <div className="flex flex-wrap gap-2 min-h-[36px]">
                    {msTeamsGroups.map((group) => (
                      <span
                        key={group}
                        className="inline-flex items-center gap-1.5 bg-purple-50 text-purple-700 text-sm px-3 py-1.5 rounded-full border border-purple-100"
                      >
                        {group}
                        <button
                          onClick={() => removeMsTeamsGroup(group)}
                          className="text-purple-400 hover:text-purple-600 transition-colors"
                        >
                          <X size={13} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Input
                      placeholder="Type a team/chat name and press Enter..."
                      value={newMsTeamsGroup}
                      onChange={(e) => setNewMsTeamsGroup(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addMsTeamsGroup())}
                      className="rounded-xl border-gray-200"
                    />
                    <Button variant="outline" size="sm" onClick={addMsTeamsGroup} className="rounded-xl shrink-0">
                      <Plus size={16} />
                    </Button>
                  </div>
                </div>
              </div>

              {/* Dynamic Requirement Categories */}
              {requirementCategories.map((category) => {
                const isSelected = (itemId: string) => selectedItems[category.id]?.has(itemId) || false;
                const selectedCount = selectedItems[category.id]?.size || 0;
                return (
                  <div key={category.id} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-lg ${category.iconBg} flex items-center justify-center`}>
                        <CategoryIcon icon={category.icon} className={`w-5 h-5 ${category.iconColor}`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-gray-900">{category.name}</h3>
                          {selectedCount > 0 && (
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                              {selectedCount} selected
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{category.description}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                      {category.items.map((item) => (
                        <label
                          key={item.id}
                          className={`flex items-center gap-3 p-4 rounded-xl border cursor-pointer transition-all ${
                            isSelected(item.id)
                              ? "border-blue-500 bg-blue-50"
                              : "border-gray-200 hover:border-gray-300"
                          }`}
                        >
                          <Checkbox
                            checked={isSelected(item.id)}
                            onCheckedChange={() => toggleRequirementItem(category.id, item.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{item.name}</p>
                            <p className="text-xs text-gray-500">{item.description}</p>
                          </div>
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded shrink-0">
                            {item.lead}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-gray-900">Review & Confirm</h2>
              <p className="text-sm text-gray-500 mt-1">Review all details before commencing the hiring process</p>
            </div>

            <div className="space-y-6">
              {/* Hiring Summary Card */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                  <h3 className="font-semibold text-gray-900">Hiring Summary</h3>
                </div>
                <div className="p-6 space-y-6">
                  {/* Candidate Info */}
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                      <User size={20} className="text-slate-600" />
                    </div>
                    <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Candidate Name</p>
                        <p className="text-sm font-semibold text-gray-900">{candidateName}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Email Address</p>
                        <p className="text-sm font-medium text-gray-900">{username}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Mobile</p>
                        <p className="text-sm font-medium text-gray-900">{candidate.phone || "Not provided"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Role</p>
                        <p className="text-sm font-medium text-gray-900">{role}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Position</p>
                        <p className="text-sm font-medium text-gray-900">{position}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Department</p>
                        <p className="text-sm font-medium text-gray-900">{department}</p>
                      </div>
                    </div>
                  </div>

                  {/* Divider */}
                  <div className="border-t border-gray-100" />

                  {/* Start Details */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">Start Date</p>
                      <div className="flex items-center gap-2 mt-1">
                        <CalendarDays size={14} className="text-gray-400" />
                        <p className="text-sm font-medium text-gray-900">
                          {startDate ? new Date(startDate + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" }) : "Not set"}
                        </p>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">User Role</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">{userRole}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">Employment Status</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">{employmentStatus}</p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">Onboarding Template</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {templates.find((t) => String(t.id) === onboardingTemplate)?.name || "Not selected"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">Training Plans</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">
                        {trainingPlans
                          .filter((p) => trainingPlanIds.includes(String(p.id)))
                          .map((p) => p.name)
                          .join(", ") || "Not selected"}
                      </p>
                    </div>
                    {selectedManagerName && (
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Manager</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">{selectedManagerName}</p>
                      </div>
                    )}
                    <div>
                      <p className="text-xs text-gray-400 uppercase tracking-wider">Show in Attendance & Leave</p>
                      <p className="text-sm font-medium text-gray-900 mt-1">{showInAttendance ? "Yes" : "No"}</p>
                    </div>
                    {showInAttendance && accrualStartDate && (
                      <div>
                        <p className="text-xs text-gray-400 uppercase tracking-wider">Start Accruing From</p>
                        <p className="text-sm font-medium text-gray-900 mt-1">
                          {new Date(accrualStartDate + "T00:00:00").toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Setup & Access Summary */}
              <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                  <h3 className="font-semibold text-gray-900">Setup & Access</h3>
                </div>
                <div className="p-6 space-y-6">
                  {/* SharePoint & Teams */}
                  {(sharePointGroups.length > 0 || msTeamsGroups.length > 0) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      {sharePointGroups.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">SharePoint Groups</p>
                          <div className="flex flex-wrap gap-2">
                            {sharePointGroups.map((group) => (
                              <span key={group} className="inline-flex items-center bg-blue-50 text-blue-700 text-xs px-2.5 py-1 rounded-full border border-blue-100">
                                {group}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {msTeamsGroups.length > 0 && (
                        <div>
                          <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">MS Teams / Group Chats</p>
                          <div className="flex flex-wrap gap-2">
                            {msTeamsGroups.map((group) => (
                              <span key={group} className="inline-flex items-center bg-purple-50 text-purple-700 text-xs px-2.5 py-1 rounded-full border border-purple-100">
                                {group}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Requirement Categories */}
                  {requirementCategories.map((category) => {
                    const selected = Array.from(selectedItems[category.id] || []);
                    if (selected.length === 0) return null;
                    return (
                      <div key={category.id}>
                        <div className="flex items-center gap-2 mb-2">
                          <div className={`w-6 h-6 rounded-md ${category.iconBg} flex items-center justify-center`}>
                            <CategoryIcon icon={category.icon} className={`w-3.5 h-3.5 ${category.iconColor}`} />
                          </div>
                          <p className="text-xs text-gray-400 uppercase tracking-wider">{category.name}</p>
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                            {selected.length}
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {selected.map((id) => {
                            const item = category.items.find((i) => i.id === id);
                            if (!item) return null;
                            return (
                              <span
                                key={id}
                                className="inline-flex items-center gap-1.5 bg-gray-50 text-gray-700 text-sm px-3 py-1.5 rounded-lg border border-gray-200"
                              >
                                {item.name}
                                <span className="text-xs text-gray-400">· {item.lead}</span>
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}

                  {/* Nothing selected message */}
                  {(() => {
                    const hasAnySelection = requirementCategories.some((cat) => (selectedItems[cat.id]?.size || 0) > 0);
                    const hasGroups = sharePointGroups.length > 0 || msTeamsGroups.length > 0;
                    if (!hasAnySelection && !hasGroups) {
                      return (
                        <p className="text-sm text-gray-400 italic">No setup or access items selected</p>
                      );
                    }
                    return null;
                  })()}
                </div>
              </div>

              {/* Notes */}
              {notes && (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
                    <h3 className="font-semibold text-gray-900">Notes for IT / HR / Finance</h3>
                  </div>
                  <div className="p-6">
                    <p className="text-sm text-gray-700 bg-amber-50 rounded-lg p-4 border border-amber-100">{notes}</p>
                  </div>
                </div>
              )}

              {/* Confirmation Checkbox */}
              <div className="bg-white rounded-2xl border border-gray-100 p-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox
                    checked={confirmReview}
                    onCheckedChange={(checked) => setConfirmReview(checked === true)}
                    className="mt-0.5"
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">I have reviewed all the details</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      By checking this box, you confirm that all the information above is correct and you are ready to commence the hiring process.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Bottom Action Bar */}
      <div className="sticky bottom-0 bg-white border-t border-gray-100 p-4 -mx-4 sm:-mx-6 lg:-mx-0">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              className="rounded-xl text-gray-500 hover:text-gray-700"
              onClick={step === 1 ? () => navigate(`/recruitment/candidates/${candidateId}`) : handleBack}
            >
              <ChevronLeft size={16} />
              {step === 1 ? "Cancel" : "Back"}
            </Button>
          </div>

          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="rounded-xl gap-2"
              onClick={saveDraft}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save size={16} />}
              Save & Exit
            </Button>

            {step === 3 && (
              <Button
                variant="ghost"
                className="rounded-xl text-gray-500 hover:text-gray-700"
                onClick={() => navigate(`/recruitment/candidates/${candidateId}`)}
              >
                Cancel
              </Button>
            )}

            {step < 3 ? (
              <Button
                className="bg-slate-900 hover:bg-slate-800 text-white rounded-xl gap-2 px-6"
                onClick={handleNext}
              >
                Next
                <ChevronLeft size={16} className="rotate-180" />
              </Button>
            ) : (
              <Button
                className="bg-green-600 hover:bg-green-700 text-white rounded-xl gap-2 px-6"
                onClick={handleSubmit}
                disabled={saving || !confirmReview}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 size={16} />}
                Commence Hiring
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CommenceHiring;
